import type {
  AssigneeOption,
  CommentView,
  QUEUE_ASSIGNEE_FILTERS,
  QueueResponse,
  RequestStatus,
  ReviewDetail,
  SourcingResponse,
} from "@procuremate/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

/** Buyer workspace (WP-3.1): queue and review page. */

export interface QueueParams {
  status: RequestStatus | "open" | "all";
  assignee: (typeof QUEUE_ASSIGNEE_FILTERS)[number];
  q: string;
  page: number;
}

export const QUEUE_PAGE_SIZE = 25;

export const reviewKeys = {
  all: ["review"] as const,
  queue: (params: QueueParams) => ["review", "queue", params] as const,
  detail: (id: string) => ["review", "detail", id] as const,
  assignees: (id: string) => ["review", "assignees", id] as const,
  sourcing: (id: string) => ["review", "sourcing", id] as const,
};

export function useQueue(params: QueueParams) {
  const search = new URLSearchParams({
    status: params.status,
    assignee: params.assignee,
    page: String(params.page),
    pageSize: String(QUEUE_PAGE_SIZE),
  });
  if (params.q) search.set("q", params.q);
  return useQuery({
    queryKey: reviewKeys.queue(params),
    queryFn: () => api<QueueResponse>(`/queue?${search.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function useReview(id: string) {
  return useQuery({ queryKey: reviewKeys.detail(id), queryFn: () => api<ReviewDetail>(`/requests/${id}/review`) });
}

export function useAssignees(id: string, enabled: boolean) {
  return useQuery({
    queryKey: reviewKeys.assignees(id),
    queryFn: () => api<AssigneeOption[]>(`/requests/${id}/assignees`),
    enabled,
  });
}

/** Every change returns the fresh review page; the queue is refreshed too. */
function useReviewMutation<TVars>(id: string, fn: (vars: TVars) => Promise<ReviewDetail>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (detail) => {
      client.setQueryData(reviewKeys.detail(id), detail);
      await client.invalidateQueries({ queryKey: ["review", "queue"] });
    },
  });
}

export const useTransition = (id: string) =>
  useReviewMutation(id, (body: { to: RequestStatus; note?: string }) =>
    api<ReviewDetail>(`/requests/${id}/transitions`, { body }),
  );

export const useAssign = (id: string) =>
  useReviewMutation(id, (userId: string | null) =>
    api<ReviewDetail>(`/requests/${id}/assignee`, { method: "PUT", body: { userId } }),
  );

export function useAddComment(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api<CommentView[]>(`/requests/${id}/comments`, { body: { body } }),
    onSuccess: (comments) => {
      client.setQueryData<ReviewDetail>(reviewKeys.detail(id), (old) => (old ? { ...old, comments } : old));
    },
  });
}

/** While an analysis is being built, its status is checked every few seconds. */
const SOURCING_POLL_MS = 4_000;

/** The sourcing analysis (WP-3.2, ADR-0027): buyers only. */
export function useSourcing(id: string) {
  return useQuery({
    queryKey: reviewKeys.sourcing(id),
    queryFn: () => api<SourcingResponse>(`/requests/${id}/sourcing`),
    refetchInterval: (query) => (query.state.data?.analysis?.status === "PENDING" ? SOURCING_POLL_MS : false),
  });
}

export function useRefreshSourcing(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<SourcingResponse>(`/requests/${id}/sourcing`, { method: "POST" }),
    onSuccess: (data) => client.setQueryData(reviewKeys.sourcing(id), data),
  });
}
